import React, { useState } from 'react';
import { Col, Row, Form, Button, InputGroup } from 'react-bootstrap';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serverUrlAuth } from '../../serverUrl';
import Logo from '../../assets/logo7.png';
import { Typewriter } from 'react-simple-typewriter';

const ActivateAccount = () => {
  const { uidb64, token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    password2: '',
    agree_to_terms: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const getPasswordStrength = (password) => {
    const hasEight = password.length >= 8;
    const hasNumbers = /\d/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (hasEight && hasNumbers && hasUppercase && hasSpecialCharacter)
      return 'strong';
    if (hasEight && (hasNumbers || hasUppercase || hasSpecialCharacter))
      return 'medium';
    return 'weak';
  };

  const getPasswordStrengthIndicator = () => {
    const strength = getPasswordStrength(formData.password);
    const indicators = {
      weak: { color: '#ef4444', text: 'Weak', width: '33%' },
      medium: { color: '#f59e0b', text: 'Medium', width: '66%' },
      strong: { color: '#10b981', text: 'Strong', width: '100%' },
    };
    return indicators[strength];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { password, password2, agree_to_terms } = formData;

    if (!agree_to_terms) {
      toast.error('You must agree to the Terms and Conditions.');
      return;
    }

    if (password !== password2) {
      toast.error('Passwords do not match.');
      return;
    }

    const passwordStrength = getPasswordStrength(password);
    const hasEight = password.length >= 8;
    const hasNumbers = /\d/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (passwordStrength === 'weak') {
      toast.error(
        <div>
          Password is too weak. It must contain:
          <ul className="list-disc pl-5 mt-1">
            {!hasEight && <li>At least 8 characters</li>}
            {!hasNumbers && <li>At least one number</li>}
            {!hasUppercase && <li>At least one uppercase letter</li>}
            {!hasSpecialCharacter && <li>At least one special character</li>}
          </ul>
        </div>,
        { autoClose: 10000 }
      );
      return;
    }

    setLoading(true);
    try {
      await serverUrlAuth.post(`users/auth/activate/${uidb64}/${token}/`, {
        password,
        agree_to_terms,
      });
      toast.success('Account activated successfully! You can now login.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      console.error('Activation error:', error);
      if (error.response?.status === 400) {
        if (
          error.response.data?.error === 'Activation link expired or invalid.'
        ) {
          toast.error('Activation link has expired. Please request a new one.');
        } else if (error.response.data?.error === 'Invalid activation link.') {
          toast.error('Invalid activation link.');
        } else if (
          error.response.data?.message === 'Account already activated.'
        ) {
          toast.info('Your account is already active. Please login.');
          setTimeout(() => navigate('/login'), 2000);
        } else {
          toast.error(
            error.response.data?.error || 'Failed to activate account.'
          );
        }
      } else {
        toast.error(
          'Network error. Please check your connection and try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    getPasswordStrength(formData.password) !== 'weak' &&
    formData.password === formData.password2 &&
    formData.agree_to_terms;

  return (
    <Row className="login-row">
      <Col md={6} className="login-left">
        <div className="overlay"></div>
        <div className="login-left-content">
          <Link to="/">
            <img src={Logo} alt="Artiza Logo" className="logo" />
          </Link>
          <h2 className="typing-text">
            <Typewriter
              words={[
                'Complete your account activation to start your journey as an artisan.',
              ]}
              loop={true}
              cursor
              cursorStyle="_"
              typeSpeed={70}
              deleteSpeed={50}
              delaySpeed={1500}
            />
          </h2>
        </div>
      </Col>

      <Col md={6} className="login-right">
        <div className="login-form-container">
          <h2>Activate Your Account</h2>
          {/* <p className="text-muted mb-4">Set your password to activate your artisan account.</p> */}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4" controlId="formPassword">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter a strong password"
                  className="form-input"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </Button>
              </InputGroup>
              {formData.password && (
                <div className="mt-2">
                  <div className="password-strength-bar">
                    <div
                      className="strength-fill"
                      style={{
                        width: getPasswordStrengthIndicator().width,
                        backgroundColor: getPasswordStrengthIndicator().color,
                      }}
                    ></div>
                  </div>
                  <small className="text-muted">
                    Strength:{' '}
                    <span
                      style={{ color: getPasswordStrengthIndicator().color }}
                    >
                      {getPasswordStrengthIndicator().text}
                    </span>
                  </small>
                  <ul className="password-requirements mt-2">
                    <li
                      className={
                        formData.password.length >= 8
                          ? 'text-success'
                          : 'text-muted'
                      }
                    >
                      At least 8 characters
                    </li>
                    <li
                      className={
                        /\d/.test(formData.password)
                          ? 'text-success'
                          : 'text-muted'
                      }
                    >
                      At least one number
                    </li>
                    <li
                      className={
                        /[A-Z]/.test(formData.password)
                          ? 'text-success'
                          : 'text-muted'
                      }
                    >
                      At least one uppercase letter
                    </li>
                    <li
                      className={
                        /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
                          ? 'text-success'
                          : 'text-muted'
                      }
                    >
                      At least one special character
                    </li>
                  </ul>
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-4" controlId="formConfirmPassword">
              <Form.Label>Confirm Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="password2"
                  placeholder="Re-enter your password"
                  className="form-input"
                  value={formData.password2}
                  onChange={handleChange}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </Button>
              </InputGroup>
              {formData.password2 &&
                formData.password !== formData.password2 && (
                  <small className="text-danger">Passwords do not match</small>
                )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formTerms">
              <Form.Check
                type="checkbox"
                name="agree_to_terms"
                label={
                  <span>
                    I agree to the{' '}
                    <Link
                      to="/terms-and-conditions"
                      target="_blank"
                      className="create-account-link"
                    >
                      Terms and Conditions
                    </Link>
                  </span>
                }
                checked={formData.agree_to_terms}
                onChange={handleChange}
              />
            </Form.Group>

            <Button
              type="submit"
              className="w-100 login-btn mb-3"
              // disabled={!isFormValid || loading}
            >
              {loading ? 'Activating...' : 'Activate Account'}
            </Button>

            <div className="text-center">
              <span>Remember your password? </span>
              <Link to="/login" className="create-account-link">
                Login here
              </Link>
            </div>
          </Form>
        </div>
      </Col>
    </Row>
  );
};

export default ActivateAccount;
